<script setup lang="ts">
import type {
  PrebuiltConfiguration,
  ProductLineDetail,
  ProductSet,
} from '~/types/api'
import HelpForm from '~/components/Product/HelpForm.vue'

// TODO: make props required once /[line]/[slug] has skeletons (not nullable)
const props = defineProps<{
  line: ProductLineDetail | null
  set: ProductSet | null
}>()

const { data: prebuiltConfigurations } = await useAsyncData(async () => {
  return await createPrebuiltConfigurationsRepository().getBySetNid(
    props.set?.nid
  )
})

const emit = defineEmits(['configureFromImage'])

const prebuildConfiguration = ref<PrebuiltConfiguration | null>(null)

if (prebuiltConfigurations.value) {
  const mainConfiguration = prebuiltConfigurations.value.items.find(
    (configuration) => configuration.main && configuration?.image
  )
  if (mainConfiguration && props.set?.readyForConfigurator) {
    prebuildConfiguration.value = mainConfiguration
  }
}

const heroImage = computed(() => {
  if (prebuildConfiguration.value) {
    return prebuildConfiguration.value.image
  } else {
    return props.set?.products[0]?.images[0]
  }
})

function loadConfiguration() {
  if (prebuildConfiguration.value) {
    emit('configureFromImage', prebuildConfiguration.value.nid)
  }
}

const helpModal = ref(false)

const route = useRoute()

const localePath = useLocalePath()
const { t } = useI18n()

const { sendCustomiseYourChairEvent } = useEvent()

function sendEvent() {
  sendCustomiseYourChairEvent(`${props.line?.title} ${props.set?.title}`)
}
</script>

<template>
  <div class="container">
    <UiBreadcrumbs
      class="xl:pt-5"
      :overrides="[
        undefined,
        {
          label: t('breadcrumb.items.product-lines.label'),
          to: localePath({ name: 'product-lines' }),
        },
        {
          label: line?.title,
          to: localePath({ name: 'slug', params: { slug: line?.urlSlug } }),
        },
      ]"
      :append="[
        {
          label: set?.title,
          to: route.path,
        },
      ]"
      rounded
    />
    <div
      class="grid max-h-[calc(100dvh-200px)] items-center gap-2 pb-10 pt-5 md:grid-cols-2 lg:gap-5 2xl:gap-10 2xl:pb-20"
    >
      <div v-if="heroImage" class="flex flex-col items-center pb-10 md:pb-0">
        <div class="aspect-square h-full overflow-hidden">
          <NuxtImg
            :alt="heroImage.metaAlt || undefined"
            :title="heroImage.metaTitle || undefined"
            :src="withHost(heroImage.url)"
            sizes="100vw lg:600px 2xl:800px"
            class="h-full w-full max-w-md object-cover sm:min-h-[300px] lg:max-w-none"
          />
        </div>
        <UiButtonBlack
          v-if="prebuildConfiguration"
          @click="loadConfiguration()"
        >
          <Icon name="fa6-regular:pen-to-square" />
          {{ $t('base.products.setDetail.hero.configureFromImage') }}
        </UiButtonBlack>
      </div>
      <div class="flex flex-col gap-12">
        <h1
          class="font-pragmatica-ext flex gap-4 text-5xl font-bold lg:text-6xl 2xl:text-8xl"
          style="align-items: last baseline"
        >
          <span
            class="ml-10 w-min first-letter:-ml-10 md:ml-16 md:first-letter:-ml-16"
          >
            {{ props.line?.title }}
          </span>
          <span class="text-2xl lg:text-2xl">
            {{ props.set?.title }}
          </span>
        </h1>
        <!--        <div class="flex flex-row flex-wrap gap-2 lg:gap-4">-->
        <!--          &lt;!&ndash; TODO: add functionality &ndash;&gt;-->
        <!--          <UiButton-->
        <!--            type="accent"-->
        <!--            icon="fa6-solid:eye"-->
        <!--            :text="$t('base.products.setDetail.hero.inShowroom')"-->
        <!--          />-->
        <!--          <UiButton-->
        <!--            type="accent"-->
        <!--            :text="$t('base.products.setDetail.hero.forRent')"-->
        <!--          />-->
        <!--          <UiButton-->
        <!--            type="accent"-->
        <!--            :text="$t('base.products.setDetail.hero.inStock')"-->
        <!--          />-->
        <!--        </div>-->
        <!-- TODO: use set description -->
        <!-- eslint-disable vue/no-v-html -->
        <div
          class="max-w-2xl"
          v-html="
            props.set?.description && props.set.description.length
              ? props.set.description
              : props.line?.description
          "
        ></div>
        <div v-if="props.set?.awards" class="flex gap-2">
          <div
            v-for="award in props.set.awards"
            :key="award.nid"
            class="w-fit rounded-sm p-2 hover:bg-neutral-100"
          >
            <!-- TODO: should be dynamic? not all chairs have red dot winner -->
            <img class="h-20 w-auto" :src="withHost(award.image?.url)" />
          </div>
        </div>
        <div class="flex flex-wrap gap-4">
          <UiButtonBlackOutline @click="helpModal = true">
            {{ $t('base.products.setDetail.hero.help') }}
          </UiButtonBlackOutline>
          <UModal
            v-model="helpModal"
            :ui="{
              width: 'w-full sm:max-w-screen-md',
              rounded: 'rounded-none',
            }"
          >
            <HelpForm
              :set-nid="set!.nid"
              :product-title="`${props.line?.title} ${props.set?.title}`"
              @close="helpModal = false"
            />
          </UModal>
          <NuxtLink :to="route.path + '#configurator'" @click="sendEvent">
            <UiButtonBlack v-if="props.set?.readyForConfigurator">
              {{ $t('base.products.setDetail.hero.configure') }}
            </UiButtonBlack>
          </NuxtLink>
        </div>
      </div>
    </div>
  </div>
</template>
